/* eslint-disable */
// @ts-nocheck

import { TrashOutline } from 'heroicons-react'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { FaArrowRotateRight } from 'react-icons/fa6'

import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'

import useServerArea from '../../../api/useServerArea'
import useServer from '../../../api/useServerMain'
import useServerProducts, {
  useServerProduct,
} from '../../../api/useServerProducts'
import AsyncComboBox from '../../../components/forms/AsyncCombobox'
import Input from '../../../components/forms/Input'
import MultiSelect from '../../../components/forms/Multiselect'
import Select from '../../../components/forms/Select'
import TextArea from '../../../components/forms/TextArea'
import ProductTypeBadge from '../../../components/misc/badges/ProductTypeBadge'
import Button from '../../../components/misc/Button'
import GenericTable from '../../../components/misc/GenericTable'
import { BtnActions } from '../../../components/misc/MultipleActBtn'
import Modal from '../../../components/modals/GenericModal'
import useProduct from '../../../hooks/useProduct'
import { SelectInterface } from '../../../interfaces/InterfacesLocal'
import {
  BasicNomenclator,
  TransformationRuleInterface,
} from '../../../interfaces/ServerInterfaces'
import { useAppSelector } from '../../../store/hooks'
import { formatCurrency } from '../../../utils/helpers'
import { translateMeasure } from '../../../utils/translate'
import { DetailProductContext } from '../DetailProductContainer'

const TransformationRules = () => {
  const { product, updateProduct } = useContext(DetailProductContext)
  const { allTransformationRules, getAllTransformationRules, isLoading } =
    useServerProduct()
  const [transformationRuleModal, setTransformationRuleModal] = useState<{
    state: boolean
    current?: number
  }>({ state: false })

  useEffect(() => {
    getAllTransformationRules(product?.id!, { page: 1 })
  }, [])

  const tableTitles = [
    'Producto destino',
    'U/M Origen',
    'U/M Destino',
    'Equivalencia',
    'Áreas',
  ]

  const tableData =
    allTransformationRules?.map((item) => ({
      rowId: item.id,
      boldRow: item.id === 0,
      deletedRow: item.id === -1,
      payload: {
        'Producto destino': item?.finalProduct?.name ?? '',
        'U/M Origen': translateMeasure(item?.baseProduct?.measure) ?? '',
        'U/M Destino': translateMeasure(item?.finalProduct?.measure) ?? '',
        Equivalencia: item?.unitaryEquivalentFraction ?? '',
        Áreas: (
          <div>
            {item?.areas?.map((area) => (
              <p className="mr-2" key={area.id}>
                {area.name}
              </p>
            )) ?? []}
          </div>
        ),
      },
    })) ?? []

  const onSubmit: SubmitHandler<Record<string, any>> = (data) => {
    const {
      printableLabelFront,
      printableLabelBack,
      expirationInDaysAfterProduction,
      productionOriginAreaId,
    } = data

    const dataToSend = {
      printableLabelFront,
      printableLabelBack,
      expirationInDaysAfterProduction,
      productionOriginAreaId,
    }

    updateProduct!(product?.id, dataToSend)
  }

  const actions: BtnActions[] = [
    {
      title: 'Nueva regla',
      action: () =>
        setTransformationRuleModal({
          state: true,
          current: undefined,
        }),
      icon: <PlusIcon className="h-5" />,
    },
  ]

  return (
    <div className="flex h-auto flex-col justify-start rounded-md border border-slate-300 p-7">
      <GenericTable
        actions={actions}
        loading={isLoading}
        rowAction={(rowId: number) =>
          setTransformationRuleModal({ state: true, current: rowId })
        }
        tableData={tableData}
        tableTitles={tableTitles}
      />
      {transformationRuleModal.state && (
        <Modal
          close={() => setTransformationRuleModal({ state: false })}
          state={transformationRuleModal.state}
        >
          <TransformationRulesModal
            close={() => setTransformationRuleModal({ state: false })}
            getAllTransformationRules={getAllTransformationRules}
            transformationRule={allTransformationRules.find(
              (item) => item.id === transformationRuleModal.current
            )}
          />
        </Modal>
      )}
    </div>
  )
}

export default TransformationRules

interface ModalProps {
  transformationRule?: TransformationRuleInterface
  close: () => void
  getAllTransformationRules: (productId: number, params?: any) => void
}
function TransformationRulesModal({
  transformationRule,
  close,
  getAllTransformationRules,
}: ModalProps) {
  const { register, handleSubmit, control } = useForm()
  const { product } = useContext(DetailProductContext)
  const {
    newTransformationRule,
    isLoading,
    deleteTransformationRule,
    updateTransformationRule,
    isFetching,
  } = useServerProducts()
  const { areas } = useAppSelector((state) => state.nomenclator)
  const { business } = useAppSelector((state) => state.init)
  const { getAreaAllFinder, areaAllFilter } = useServerArea()

  useEffect(() => {
    getAreaAllFinder({ type: 'STOCK', inOrganization: true }, true)
  }, [])

  const areasSelect: SelectInterface[] =
    areaAllFilter.length > 0
      ? (areaAllFilter
          ?.filter((i) => i.type === 'STOCK')
          .map((a) => ({
            id: a?.id ?? null,
            name:
              a.business?.id === business!.id
                ? a?.name
                : `${a?.name} (${a.business?.name})`,
          })) ?? [])
      : (areas
          .filter((i) => i.type === 'STOCK')
          .map((a) => ({
            id: a?.id ?? null,
            name: a?.name ?? '',
          })) ?? [])

  const onSubmit: SubmitHandler<Record<string, any>> = (data: any) => {
    if (transformationRule) {
      updateTransformationRule(transformationRule!.id, data, () => {
        close()
        getAllTransformationRules(product!.id, { page: 1 })
      })
    } else {
      newTransformationRule(data, product!.id, () => {
        close()
        getAllTransformationRules(product!.id, { page: 1 })
      })
    }
  }

  return (
    <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
      <div className="flex w-full items-center justify-between pe-6 text-center">
        {transformationRule ? (
          <h5 className="text-lg font-bold text-gray-600">
            Editar regla de transformación
          </h5>
        ) : (
          <h5 className="text-lg font-bold text-gray-600">
            Nueva regla de transformación
          </h5>
        )}
      </div>
      <AsyncComboBox
        defaultItem={
          transformationRule
            ? {
                id: transformationRule?.finalProduct?.id,
                name: transformationRule?.finalProduct?.name,
              }
            : undefined
        }
        defaultValue={transformationRule?.finalProduct?.id ?? undefined}
        label="Producto"
        name="finalProductId"
        normalizeData={{ id: 'id', name: 'name' }}
        rules={{
          required: {
            value: true,
            message: 'Este campo es requerido',
          },
        }}
        control={control}
        // id : cuba
        dataQuery={{
          url: '/administration/product/finder',
          defaultParams: {
            inOrganization: true,
            type: 'STOCK,RAW,MANUFACTURED,WASTE,ASSET,INPUT',
          },
        }}
      />
      <Input
        allowComma
        label="Fracción unitaria de equivalencia"
        type="number"
        {...register('unitaryEquivalentFraction', { required: true })}
        control={control}
        defaultValue={transformationRule?.unitaryEquivalentFraction ?? ''}
      />
      <MultiSelect
        byDefault={transformationRule?.areas.map((area) => area.id) ?? []}
        control={control}
        data={areasSelect}
        defaultValue={transformationRule?.areas.map((area) => area.id) ?? []}
        label={'Áreas'}
        name={'listAreas'}
      />
      <div className="flex justify-end gap-2">
        {!!transformationRule && (
          <button
            className={
              'rounded-lg border border-red-500 p-2 transition-colors hover:bg-red-50'
            }
            onClick={() =>
              deleteTransformationRule(transformationRule.id, () => {
                close()
                getAllTransformationRules(product!.id, { page: 1 })
              })
            }
          >
            {isFetching ? (
              <div className={'flex items-center justify-center space-x-1'}>
                <FontAwesomeIcon
                  aria-hidden="true"
                  className={'animate-spin'}
                  color={'#dc2626'}
                  height={20}
                  icon={faSpinner}
                  width={20}
                />
                <p className={'text-xs text-red-500'}>Eliminando</p>
              </div>
            ) : (
              <div className={'flex items-center justify-center space-x-1'}>
                <TrashIcon color={'#dc2626'} height={20} width={20} />
                <p className={'text-xs text-red-500'}>Eliminar</p>
              </div>
            )}
          </button>
        )}
        <Button
          color="slate-600"
          loading={isLoading}
          name={`${transformationRule ? 'Actualizar' : 'Crear'}`}
          type="submit"
        />
      </div>
    </form>
  )
}
